import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { NO_COACH_ID, ANY_COACH_ID, API_BASE } from '../constants';
import { t } from '../i18n/translations';
import {
    fetchCalendar, fetchShips, fetchCoaches,
    findSessions as apiFindSessions,
    createBooking, createMonobankInvoice,
} from '../services/api';
import { parseTime, arraysEqual } from '../utils/slots';

const MONOBANK_TOKEN = process.env.REACT_APP_MONOBANK_TOKEN;

/**
 * All booking state and handlers for the Picker widget.
 * Returns a flat object consumed directly by Picker (and its sub-components).
 */
export default function usePickerLogic() {
    // ── Step control ─────────────────────────────────────────────────────────
    const [showStep, setShowStep] = useState('search');
    const [sessionError, setSessionError] = useState('');
    const [step3Summary, setStep3Summary] = useState(null);
    const [show, setShow] = useState('Tool'); // 'Tool' | 'Checkout' (legacy, kept for safety)

    // ── Calendar ──────────────────────────────────────────────────────────────
    const [selectedDate, setSelectedDate] = useState(dayjs(new Date()));
    const [maxDate, setMaxDate] = useState(dayjs(new Date('2122-05-21')));
    const [disabeledDays, setDisabeledDays] = useState([]);

    // ── Boats ─────────────────────────────────────────────────────────────────
    const [boats, setBoats] = useState([]);
    const [selectedBoat, setSelectedBoat] = useState(NO_COACH_ID);
    const [boatsStatus, setBoatsStatus] = useState('');

    // ── Coaches ───────────────────────────────────────────────────────────────
    const [coaches, setCoaches] = useState([{ id: NO_COACH_ID, name: t('ua', 'noCoach') }]);
    const [selectedCoach, setSelectedCoach] = useState(NO_COACH_ID);
    const [coachesStatus, setCoachesStatus] = useState('');

    // ── Riders / beginners / duration ─────────────────────────────────────────
    const [riders] = useState([1, 2, 3, 4, 5, 6]);
    const [selectedRiders, setSelectedRiders] = useState(1);
    const [beginners, setBeginners] = useState([0, 1]);
    const [selectedBeginners, setSelectedBeginners] = useState(0);
    const [duration, setDuration] = useState([
        10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
        95, 100, 105, 110, 115, 120, 125, 130, 135, 140, 145, 150,
    ]);
    const [selectedDuration, setSelectedDuration] = useState(10);

    // ── Time slots ────────────────────────────────────────────────────────────
    const [timeFrames, setTimeFrames] = useState([]);
    const [selectedStart, setSelectedStart] = useState('');
    const [selectedEnd, setSelectedEnd] = useState('');
    const [startTimes, setStartTimes] = useState([]);
    const [endTimes, setEndTimes] = useState([]);

    // ── Pricing ───────────────────────────────────────────────────────────────
    const [coachPrice, setCoachPrice] = useState();
    const [boatPrice, setBoatPrice] = useState();
    const [totalPrice, setTotalPrice] = useState();
    const [paymentType, setPaymentType] = useState('full');

    // ── Rider identity ────────────────────────────────────────────────────────
    const [riderName, setRiderName] = useState('');
    const [riderPhone, setRiderPhone] = useState('+380');
    const [riderFound, setRiderFound] = useState(false);
    const [riderBalance, setRiderBalance] = useState(0);

    // ── Session status ────────────────────────────────────────────────────────
    // Values: 'Not Yet Found' | 'Looking' | 'Found' | 'Not Found' | 'Error'
    const [sessionStatus, setSessionStatus] = useState('Not Yet Found');
    const [findValidation, setFindValidation] = useState(true);

    // ── Booking result ────────────────────────────────────────────────────────
    const [successfullyBooked, setSuccessfullyBooked] = useState(false);
    const [errorBooking, setErrorBooking] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');
    const [final, setFinal] = useState({});

    // ── UI misc ───────────────────────────────────────────────────────────────
    const [lang, setLang] = useState('ua');
    const [coachAlert, setCoachAlert] = useState(false);
    const [comment, setComment] = useState('');

    // ── Effects ───────────────────────────────────────────────────────────────

    // Re-label boats/coaches when language switches
    // eslint-disable-next-line
    useEffect(() => {
        const indexBoats = boats.findIndex(o => o.id === NO_COACH_ID);
        if (indexBoats !== -1) {
            setBoats(boats.map(o => {
                if (o.id === NO_COACH_ID) o.name = t(lang, 'anyBoat');
                return o;
            }));
        }

        const indexAnyCoach = coaches.findIndex(o => o.id === ANY_COACH_ID);
        if (indexAnyCoach !== -1) {
            setCoaches(coaches.map(o => {
                if (o.id === ANY_COACH_ID) o.name = t(lang, 'anyCoach');
                return o;
            }));
        }

        const indexNoCoach = coaches.findIndex(o => o.id === NO_COACH_ID);
        if (indexNoCoach !== -1) {
            setCoaches(coaches.map(o => {
                if (o.id === NO_COACH_ID) o.name = t(lang, 'noCoach');
                return o;
            }));
        }
    // eslint-disable-next-line
    }, [lang]);

    // Derive startTimes list from timeFrames
    // eslint-disable-next-line
    useEffect(() => {
        setStartTimes(timeFrames.map(frame => frame[0]));
    }, [timeFrames]);

    // Validation: Go to booking requires name, phone, and a coach
    // eslint-disable-next-line
    useEffect(() => {
        const valid = riderName.length > 1 && riderPhone.replace('+', '').length > 11 && selectedCoach !== '';
        setFindValidation(!valid);
    }, [riderName, riderPhone, selectedCoach]);

    // Initial data fetch
    // eslint-disable-next-line
    useEffect(() => {
        const fetchData = async () => {
            setBoatsStatus('Getting boats');
            setCoachesStatus('Getting coaches');
            try {
                const [calendarData, shipsData, coachesData] = await Promise.all([
                    fetchCalendar(),
                    fetchShips(),
                    fetchCoaches(selectedDate.toISOString().split('T')[0]),
                ]);
                setBoatsStatus('');
                setCoachesStatus('');
                setMaxDate(dayjs(new Date(calendarData.lastDay)));
                setDisabeledDays(calendarData.disabledDays);
                setBoats([...shipsData.ships]);
                setSelectedBoat(shipsData.ships[0].id);
                setCoaches([{ id: NO_COACH_ID, name: t(lang, 'noCoach') }, ...coachesData.coaches]);
            } catch (error) {
                console.error('Error fetching data:', error);
                if (error.response && error.response.status === 500) {
                    alert('Internal server error occurred. Please try again later.');
                } else {
                    alert('An error occurred while fetching data. Please try again later.');
                }
            }
        };
        fetchData();
    // eslint-disable-next-line
    }, []);

    // Price calculation
    // eslint-disable-next-line
    useEffect(() => {
        const balance = riderBalance ?? 0;
        if (coachPrice + boatPrice >= balance) {
            setTotalPrice(paymentType === 'part'
                ? (coachPrice + boatPrice - balance) / 2
                : coachPrice + boatPrice - balance);
        } else {
            setTotalPrice(0);
        }
    }, [coachPrice, boatPrice, riderBalance, paymentType]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleChangeLanguage = (event) => setLang(event.target.value);

    const handleDateChange = async (date) => {
        setSelectedDate(date);
        setCoachesStatus('Getting coaches');
        const coachesData = await fetchCoaches(date.toISOString().split('T')[0]);
        const availableCoaches = coachesData.coaches;

        if (availableCoaches.length > 0) {
            setCoaches(selectedBeginners > 0
                ? [...coachesData.coaches]
                : [{ id: NO_COACH_ID, name: t(lang, 'noCoach') }, ...coachesData.coaches]);
            if (selectedCoach === '') setSelectedCoach(coachesData.coaches[0].id);
            setCoachesStatus('');
        } else {
            if (selectedBeginners > 0) {
                setSelectedCoach('');
                setCoachesStatus('No coaches');
            } else {
                setCoaches([{ id: NO_COACH_ID, name: 'Без тренера' }]);
                setSelectedCoach(NO_COACH_ID);
                setCoachesStatus('');
            }
        }
    };

    const disableDay = (date) => {
        const dis = (day) => date.setHours(0, 0, 0, 0) === dayjs(new Date(day)).setHours(0, 0, 0, 0);
        return disabeledDays.some(dis);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === 'payment-type') { setPaymentType(value); return; }
        if (name === 'boats')   { setSelectedBoat(value); return; }
        if (name === 'coaches') { setSelectedCoach(value); return; }
        if (name === 'duration') { setSelectedDuration(value); return; }
        if (name === 'end')   { setSelectedEnd(value); return; }

        if (name === 'name') { setRiderName(value); return; }

        if (name === 'comment') {
            setComment(value.length <= 300 ? value : value.slice(0, 300));
            return;
        }

        if (name === 'phone') {
            if (value.replace('+', '').length < 13) {
                if (value.match(/^[+-]?\d+$/) || value === '' || value === '+') {
                    setRiderPhone(value);
                }
            }
            return;
        }

        if (name === 'riders') {
            const n = parseInt(value, 10);
            setSelectedRiders(n);
            setBeginners(Array.from({ length: n + 1 }, (_, i) => i));
            if (selectedBeginners > n) setSelectedBeginners(n);
            const newDurations = Array.from({ length: 31 - n * 2 }, (_, i) => i * 5 + n * 10);
            setDuration(newDurations);
            if (selectedDuration < n * 10) setSelectedDuration(n * 10);
            return;
        }

        if (name === 'beginners') {
            setSelectedBeginners(value);
            if (value > 0) {
                if (selectedCoach === NO_COACH_ID && coaches.length > 1) {
                    setSelectedCoach(coaches[1].id);
                    setCoachAlert(true);
                }
                if (selectedCoach === NO_COACH_ID && coaches.length <= 1) {
                    setSelectedCoach('');
                    setCoachesStatus('No coaches');
                    setCoachAlert(true);
                }
                setCoaches(coaches.filter(c => c.id !== NO_COACH_ID));
            } else {
                if (coaches.length <= 1) {
                    setCoaches([{ id: NO_COACH_ID, name: 'Без тренера' }]);
                    setSelectedCoach(NO_COACH_ID);
                    setCoachesStatus('');
                }
            }
            if (value === 0 && !coaches.some(c => c.id === NO_COACH_ID)) {
                setCoaches([{ id: NO_COACH_ID, name: t(lang, 'noCoach') }, ...coaches]);
            }
            return;
        }

        if (name === 'start') {
            setSelectedStart(value);
            timeFrames.forEach(frame => {
                if (frame[0] === value) {
                    setSelectedEnd(frame[1]);
                    setEndTimes([frame[1]]);
                    setCoachPrice(frame[2]);
                    setBoatPrice(frame[3]);
                }
            });
        }
    };

    const resetEverything = () => {
        setSelectedDate(dayjs(new Date()));
        setSelectedBoat(NO_COACH_ID);
        setSelectedCoach(NO_COACH_ID);
        setSelectedRiders(1);
        setSelectedBeginners(0);
        setSelectedDuration(10);
        setTimeFrames([]);
        setSelectedStart('');
        setSelectedEnd('');
        setCoachPrice(undefined);
        setBoatPrice(undefined);
        setTotalPrice(undefined);
        setRiderName('');
        setRiderPhone('38');
        setSessionStatus('Not Yet Found');
        setFindValidation(true);
        setRiderFound(false);
        setRiderBalance(0);
        setComment('');
        setCheckoutError('');
    };

    const findSessions = async () => {
        if (selectedBeginners > 0 && (selectedCoach === '' || selectedCoach === NO_COACH_ID)) {
            setCoachAlert(true);
            setSessionError(t(lang, 'coachRequiredForBeginners'));
            return;
        }
        setSelectedEnd('');
        setSelectedStart('');
        setSessionError('');
        setSessionStatus('Looking');
        setTimeFrames([]);
        const data = {
            day: selectedDate.toISOString().split('T')[0],
            shipID: selectedBoat,
            coachID: selectedCoach,
            ridersQty: selectedRiders,
            beginnersQty: selectedBeginners,
        };
        try {
            const send = await apiFindSessions(data);
            if (send.isFound) {
                setRiderFound(true);
                setRiderBalance(send.balance);
            } else {
                setRiderFound(false);
                setRiderBalance(0);
            }
            if (send.timeFrames?.length > 0) {
                setSessionStatus('Found');
                setTimeFrames(send.timeFrames.map(f => [f.startTime, f.endTime, f.coachPrice, f.boatPrice]));
            } else {
                setSessionStatus('Not Found');
            }
        } catch {
            setSessionStatus('Error');
            setSessionError('Session search failed. Please try again.');
        }
    };

    const handleGoToBooking = async () => {
        if (selectedBeginners > 0 && (selectedCoach === '' || selectedCoach === NO_COACH_ID)) {
            setCoachAlert(true);
            setSessionError(t(lang, 'coachRequiredForBeginners'));
            return;
        }
        setSessionError('');
        setStep3Summary(null);
        setShowStep('summary');

        const sessionStart = Array.isArray(selectedStart) && selectedStart.length > 0
            ? selectedStart[0] : selectedStart;
        let sessionEnd = '';
        if (Array.isArray(selectedStart) && selectedStart.length > 0 && timeFrames.length > 0) {
            const lastStart = selectedStart[selectedStart.length - 1];
            sessionEnd = timeFrames.find(f => f[0] === lastStart)?.[1] || '';
        } else {
            sessionEnd = selectedEnd || '';
        }

        const startMin = parseTime(sessionStart);
        const endMin = parseTime(sessionEnd);
        const durationMin = (startMin !== null && endMin !== null && endMin > startMin)
            ? endMin - startMin : 0;

        setSelectedDuration(durationMin);

        const sessionData = {
            day: selectedDate.toISOString().split('T')[0],
            shipID: selectedBoat,
            coachID: selectedCoach,
            ridersQty: selectedRiders,
            beginnersQty: selectedBeginners,
            name: riderName,
            phone: riderPhone.startsWith('+') ? riderPhone : `+${riderPhone}`,
            slots: Array.isArray(selectedStart) ? selectedStart : [selectedStart],
            startDate: selectedDate.toISOString().split('T')[0],
            sessionDuration: durationMin,
        };

        try {
            setSessionStatus('Looking');
            const response = await apiFindSessions(sessionData);
            if (response.timeFrames?.length > 0) {
                const firstStart = Array.isArray(selectedStart) && selectedStart.length > 0
                    ? selectedStart[0] : selectedStart;
                const firstFrame = response.timeFrames.find(f => f.startTime === firstStart);
                const coachPriceVal = firstFrame ? firstFrame.coachPrice : 0;
                const boatPriceVal  = firstFrame ? firstFrame.boatPrice  : 0;

                setCoachPrice(coachPriceVal);
                setBoatPrice(boatPriceVal);
                setRiderBalance(response.balance ?? 0);

                const finalBoat  = boats.find(b => b.id === selectedBoat)?.name || '';
                const finalCoach = coaches.find(c => c.id === selectedCoach)?.name || '';

                const slots = Array.isArray(selectedStart)
                    ? selectedStart : (selectedStart ? [selectedStart] : []);
                const endTime = Array.isArray(selectedStart) && selectedStart.length > 0 && timeFrames.length > 0
                    ? (timeFrames.find(f => f[0] === selectedStart[selectedStart.length - 1])?.[1] || selectedEnd)
                    : (selectedEnd || '');

                const summary = {
                    date:       selectedDate.toISOString().split('T')[0],
                    boat:       finalBoat,
                    boatId:     selectedBoat,
                    coach:      finalCoach,
                    coachId:    selectedCoach,
                    riders:     selectedRiders,
                    beginners:  selectedBeginners,
                    duration:   durationMin,
                    name:       riderName,
                    phone:      riderPhone.replace('+', ''),
                    coachPrice: coachPriceVal,
                    boatPrice:  boatPriceVal,
                    balance:    response.balance ?? 0,
                    slots,
                    start:      firstStart,
                    end:        endTime,
                };
                setStep3Summary(summary);
                setFinal(summary);
                setSessionStatus('Found');
            } else {
                setSessionStatus('Not Found');
                setSessionError(t(lang, 'sessionNotFound'));
                setShowStep('search');
            }
        } catch {
            setSessionStatus('Error');
            setSessionError(t(lang, 'sessionCheckError'));
            setShowStep('search');
        }
    };

    const order = async () => {
        const firstStart = Array.isArray(selectedStart)
            ? selectedStart[0]
            : (typeof selectedStart === 'string' && selectedStart.includes(',')
                ? selectedStart.split(',')[0].trim() : selectedStart);

        const bookingPayload = {
            phone: riderPhone.replace('+', ''),
            startDate: `${selectedDate.toISOString().split('T')[0]} ${firstStart}`,
            shipId: selectedBoat,
            coachId: selectedCoach,
            sessionDuration: selectedDuration,
            ridersQty: selectedRiders,
            name: riderName,
            comment,
            beginnersQty: selectedBeginners,
        };

        const bookingStatus = await createBooking(bookingPayload);
        console.log(bookingStatus.orderId);

        if (bookingStatus.orderId === 0) {
            setErrorBooking(true);
            return;
        }

        try {
            const invoiceStart = firstStart;
            const response = await createMonobankInvoice(
                {
                    amount: parseInt(totalPrice) * 100,
                    destination: `${t(lang, 'invoiceBooked')} ${final.date || selectedDate.toISOString().split('T')[0]}. ${t(lang, 'invoiceSessionStart')} ${invoiceStart}, ${t(lang, 'invoiceDuration')} ${final.duration ?? selectedDuration} ${t(lang, 'minutes')}, ${final.riders ?? selectedRiders} ${t(lang, 'invoiceRiders')}`,
                    reference: `${bookingStatus.orderId}`,
                    redirectUrl: window.location.origin,
                    webhookUrl: `${API_BASE}/callback`,
                },
                MONOBANK_TOKEN,
            );
            window.location.href = response.pageUrl;
            setCheckoutError('');
        } catch (error) {
            console.log(error.response?.data?.errText);
            setCheckoutError(t(lang, 'checkoutError'));
        }
    };

    const handleClose = (type) => {
        if (type === 'success') { resetEverything(); setSuccessfullyBooked(false); }
        if (type === 'error')   { setErrorBooking(false); }
        if (type === 'coachAlert') { setCoachAlert(false); }
    };

    // ── Derived values ────────────────────────────────────────────────────────

    const isSynced = (() => {
        if (!step3Summary) return true;
        const currentSlots = Array.isArray(selectedStart) ? selectedStart : (selectedStart ? [selectedStart] : []);
        return (
            step3Summary.date     === selectedDate.toISOString().split('T')[0] &&
            step3Summary.boatId   === selectedBoat &&
            step3Summary.coachId  === selectedCoach &&
            step3Summary.riders   === selectedRiders &&
            step3Summary.beginners=== selectedBeginners &&
            arraysEqual(step3Summary.slots || [], currentSlots)
        );
    })();

    // ── Return all state + handlers ───────────────────────────────────────────
    return {
        // step control
        show, setShow, showStep, sessionError,

        // calendar
        selectedDate, maxDate, disableDay,

        // boats
        boats, selectedBoat, boatsStatus,

        // coaches
        coaches, selectedCoach, coachesStatus,

        // riders / beginners / duration
        riders, selectedRiders, beginners, selectedBeginners, duration, selectedDuration,

        // time slots
        timeFrames, selectedStart, setSelectedStart, selectedEnd, startTimes, endTimes,

        // pricing
        coachPrice, boatPrice, totalPrice, paymentType,

        // rider identity
        riderName, riderPhone, riderFound, riderBalance,

        // session status
        sessionStatus, findValidation,

        // booking result
        successfullyBooked, errorBooking, checkoutError,
        final, step3Summary, isSynced,

        // UI misc
        lang, coachAlert, comment,

        // handlers
        handleChangeLanguage, handleDateChange,
        handleChange, handleGoToBooking,
        findSessions, order, handleClose,
    };
}
